/**
 * Authentication Context for MarIA Health Care
 *
 * Implements Firebase Authentication with backend /api/init integration.
 * Supports both Anonymous and Google Sign-In authentication.
 *
 * Flow:
 * 1. User accepts GDPR terms on LandingPage
 * 2. Frontend calls signInAnonymously() OR signInWithPopup() to Firebase
 * 3. Frontend sends Firebase ID token to /api/init
 * 4. Backend verifies token, creates/retrieves user, returns session info
 * 5. If consent given, frontend calls /api/consent
 * 6. User can now access /chat (if survey completed)
 *
 * UPDATE (2026-01-14): Added surveyCompleted for Dual Memory pattern.
 * Users must complete health survey before accessing chat to ensure
 * critical medical information (allergies, conditions) is available.
 *
 * @see Issue #177 - Frontend Firebase Auth Integration
 * @see PLAN_IMPLEMENTACION.md - Dual Memory Pattern
 */
import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback, useRef } from 'react';
import {
    auth,
    onAuthStateChanged,
    signInAnonymouslyToFirebase,
    signInWithGooglePopup,
    signOutFirebase,
    User
} from '@/lib/firebase';
import api from '@/lib/axios';
import { logger } from '@/lib/logger';

// Response from backend /api/init
interface InitResponse {
    user_id: string;
    firebase_uid: string;
    is_new_user: boolean;
    consent_required: boolean;
    session_id: string | null;
    survey_completed?: boolean;  // Added for Dual Memory
}

// Response from backend /api/consent
interface ConsentResponse {
    status: 'consent_recorded' | 'consent_declined';
    retention_until?: string;
    message?: string;
}

// Response from backend /api/survey/status
interface SurveyStatusResponse {
    completada: boolean;
    campos_criticos_completos?: boolean;
}

// Internal user state combining Firebase + Backend data
export interface AuthUser {
    id: string;
    firebaseUid: string;
    isAnonymous: boolean;
    isNewUser: boolean;
    consentRequired: boolean;
    surveyCompleted: boolean;  // Added for Dual Memory
    displayName?: string;
    email?: string;
    photoURL?: string;
}

// Options for authentication
interface AuthOptions {
    recordConsent?: boolean;
    retentionDays?: number;
    /** Autodeclaración de edad >= 14 años (LOPDGDD art. 7). */
    ageConfirmed?: boolean;
}

interface AuthContextType {
    user: AuthUser | null;
    firebaseUser: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    consentRequired: boolean;
    surveyCompleted: boolean;  // Added for Dual Memory
    error: string | null;

    // Actions
    initializeAuth: (options?: AuthOptions) => Promise<void>;
    signInWithGoogle: (options?: AuthOptions) => Promise<void>;
    recordConsent: (consentGiven: boolean, retentionDays?: number, ageConfirmed?: boolean) => Promise<ConsentResponse>;
    logout: () => Promise<void>;
    clearError: () => void;
    checkSurveyStatus: () => Promise<void>;  // Added for Dual Memory

    // Token access for ADK integration
    getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [consentRequired, setConsentRequired] = useState(false);
    const [surveyCompleted, setSurveyCompleted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const firebaseUserRef = useRef<User | null>(null);

    const getIdToken = useCallback(async (): Promise<string | null> => {
        const currentUser = firebaseUserRef.current || firebaseUser;
        if (!currentUser) {
            logger.warn('getIdToken called but no Firebase user available');
            return null;
        }
        try {
            return await currentUser.getIdToken(true);
        } catch (err) {
            logger.error('Failed to get ID token:', err);
            return null;
        }
    }, [firebaseUser]);

    /**
     * Check survey completion status from backend
     * Part of Dual Memory pattern - ensures profile exists before chat
     */
    const checkSurveyStatus = useCallback(async () => {
        const token = await getIdToken();
        if (!token) return;

        try {
            const response = await api.get<SurveyStatusResponse>('/survey/status', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const completed = response.data.completada;
            setSurveyCompleted(completed);

            if (user) {
                setUser({ ...user, surveyCompleted: completed });
            }

            logger.info(`Survey status checked: completed=${completed}`);
        } catch (err) {
            logger.error('Failed to check survey status:', err);
            // Default to false if we can't check - user will be redirected to survey
            setSurveyCompleted(false);
        }
    }, [getIdToken, user]);

    /**
     * Initialize backend after Firebase auth (shared logic)
     */
    const initializeBackend = useCallback(async (
        fbUser: User,
        shouldRecordConsent: boolean,
        retentionDays: number,
        ageConfirmed: boolean = false
    ): Promise<void> => {
        firebaseUserRef.current = fbUser;
        logger.info(`Firebase sign-in successful: ${fbUser.uid} (anonymous: ${fbUser.isAnonymous})`);

        const idToken = await fbUser.getIdToken(true);

        logger.info('Calling backend /api/init...');
        const response = await api.post<InitResponse>('/init', {
            firebase_token: idToken
        });

        const initData = response.data;
        logger.info(`Backend init successful: user_id=${initData.user_id}`);

        let finalConsentRequired = initData.consent_required;

        if (shouldRecordConsent && initData.consent_required) {
            logger.info('Recording GDPR consent...');
            try {
                const consentToken = await fbUser.getIdToken(true);
                const consentResponse = await api.post<ConsentResponse>('/consent', {
                    firebase_token: consentToken,
                    consent_given: true,
                    retention_days: retentionDays,
                    // LOPDGDD art. 7: consentimiento válido solo a partir de 14 años.
                    age_confirmed: ageConfirmed
                });
                if (consentResponse.data.status === 'consent_recorded') {
                    logger.info('GDPR consent recorded successfully');
                    finalConsentRequired = false;
                }
            } catch (consentErr) {
                logger.error('Failed to record consent:', consentErr);
            }
        }

        // Check survey status after init
        const surveyStatus = initData.survey_completed ?? false;

        setUser({
            id: initData.user_id,
            firebaseUid: initData.firebase_uid,
            isAnonymous: fbUser.isAnonymous,
            isNewUser: initData.is_new_user,
            consentRequired: finalConsentRequired,
            surveyCompleted: surveyStatus,
            displayName: fbUser.displayName || undefined,
            email: fbUser.email || undefined,
            photoURL: fbUser.photoURL || undefined
        });
        setFirebaseUser(fbUser);
        setConsentRequired(finalConsentRequired);
        setSurveyCompleted(surveyStatus);

        localStorage.setItem('maria_user_id', initData.user_id);
        localStorage.setItem('maria_firebase_uid', initData.firebase_uid);

        // Double-check survey status from dedicated endpoint
        // This handles cases where /api/init doesn't include survey_completed yet
        if (!surveyStatus) {
            try {
                // FIX: Add Authorization header to prevent 401 error
                const statusResponse = await api.get<SurveyStatusResponse>('/survey/status', {
                    headers: { Authorization: `Bearer ${idToken}` }
                });
                if (statusResponse.data.completada) {
                    setSurveyCompleted(true);
                    setUser(prev => prev ? { ...prev, surveyCompleted: true } : null);
                }
            } catch {
                // Endpoint might not exist yet, ignore
            }
        }
    }, []);

    /**
     * Handle authentication errors
     */
    const handleAuthError = useCallback((err: unknown) => {
        logger.error('Authentication failed:', err);

        let errorMessage = 'Error al iniciar sesión. Por favor, intenta de nuevo.';

        if (err instanceof Error) {
            if (err.message.includes('auth/operation-not-allowed')) {
                errorMessage = 'Método de autenticación no habilitado.';
            } else if (err.message.includes('auth/network-request-failed')) {
                errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
            } else if (err.message.includes('auth/popup-closed-by-user')) {
                errorMessage = 'Inicio de sesión cancelado.';
            } else if (err.message.includes('auth/popup-blocked')) {
                errorMessage = 'Ventana emergente bloqueada. Permite las ventanas emergentes.';
            }
        }

        if (typeof err === 'object' && err !== null && 'response' in err) {
            const axiosErr = err as { response?: { data?: { detail?: string }, status?: number } };
            if (axiosErr.response?.data?.detail) {
                errorMessage = axiosErr.response.data.detail;
            } else if (axiosErr.response?.status === 401) {
                errorMessage = 'Token inválido. Por favor, intenta de nuevo.';
            }
        }

        setError(errorMessage);
        setUser(null);
        setFirebaseUser(null);
        firebaseUserRef.current = null;
    }, []);

    /**
     * Sign in anonymously
     */
    const initializeAuth = useCallback(async (options?: AuthOptions) => {
        const { recordConsent: shouldRecordConsent = false, retentionDays = 365, ageConfirmed = false } = options || {};
        setIsLoading(true);
        setError(null);

        try {
            logger.info('Starting Firebase anonymous sign-in...');
            const credential = await signInAnonymouslyToFirebase();
            await initializeBackend(credential.user, shouldRecordConsent, retentionDays, ageConfirmed);
        } catch (err) {
            handleAuthError(err);
        } finally {
            setIsLoading(false);
        }
    }, [initializeBackend, handleAuthError]);

    /**
     * Sign in with Google using popup
     * Following official Firebase pattern from google-popup.ts quickstart
     */
    const signInWithGoogle = useCallback(async (options?: AuthOptions) => {
        const { recordConsent: shouldRecordConsent = false, retentionDays = 365, ageConfirmed = false } = options || {};
        setIsLoading(true);
        setError(null);

        try {
            logger.info('Starting Google sign-in...');
            const credential = await signInWithGooglePopup();
            await initializeBackend(credential.user, shouldRecordConsent, retentionDays, ageConfirmed);
        } catch (err) {
            handleAuthError(err);
        } finally {
            setIsLoading(false);
        }
    }, [initializeBackend, handleAuthError]);

    const recordConsent = useCallback(async (
        consentGiven: boolean,
        retentionDays: number = 365,
        ageConfirmed: boolean = false
    ): Promise<ConsentResponse> => {
        const currentUser = firebaseUserRef.current || firebaseUser;
        if (!currentUser) {
            throw new Error('No authenticated user');
        }

        try {
            const idToken = await currentUser.getIdToken(true);
            const response = await api.post<ConsentResponse>('/consent', {
                firebase_token: idToken,
                consent_given: consentGiven,
                retention_days: retentionDays,
                age_confirmed: ageConfirmed
            });

            if (response.data.status === 'consent_recorded') {
                setConsentRequired(false);
                if (user) {
                    setUser({ ...user, consentRequired: false });
                }
            }

            return response.data;
        } catch (err) {
            logger.error('Failed to record consent:', err);
            throw err;
        }
    }, [firebaseUser, user]);

    const logout = useCallback(async () => {
        try {
            await signOutFirebase();
        } catch (err) {
            logger.error('Firebase sign-out error:', err);
        }

        localStorage.removeItem('maria_user_id');
        localStorage.removeItem('maria_firebase_uid');
        setUser(null);
        setFirebaseUser(null);
        firebaseUserRef.current = null;
        setConsentRequired(false);
        setSurveyCompleted(false);
        setError(null);
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            firebaseUserRef.current = fbUser;

            if (fbUser) {
                setFirebaseUser(fbUser);

                const storedUserId = localStorage.getItem('maria_user_id');
                const storedFirebaseUid = localStorage.getItem('maria_firebase_uid');

                if (storedUserId && storedFirebaseUid === fbUser.uid) {
                    logger.info('Restoring session from localStorage');

                    try {
                        const idToken = await fbUser.getIdToken(true);
                        const response = await api.post<InitResponse>('/init', {
                            firebase_token: idToken
                        });

                        const surveyStatus = response.data.survey_completed ?? false;

                        setUser({
                            id: response.data.user_id,
                            firebaseUid: response.data.firebase_uid,
                            isAnonymous: fbUser.isAnonymous,
                            isNewUser: false,
                            consentRequired: response.data.consent_required,
                            surveyCompleted: surveyStatus,
                            displayName: fbUser.displayName || undefined,
                            email: fbUser.email || undefined,
                            photoURL: fbUser.photoURL || undefined
                        });
                        setConsentRequired(response.data.consent_required);
                        setSurveyCompleted(surveyStatus);

                        // Double-check survey status
                        if (!surveyStatus) {
                            try {
                                // FIX: Add Authorization header to prevent 401 error
                                const statusResponse = await api.get<SurveyStatusResponse>('/survey/status', {
                                    headers: { Authorization: `Bearer ${idToken}` }
                                });
                                if (statusResponse.data.completada) {
                                    setSurveyCompleted(true);
                                    setUser(prev => prev ? { ...prev, surveyCompleted: true } : null);
                                }
                            } catch {
                                // Ignore
                            }
                        }

                    } catch (err) {
                        logger.error('Failed to restore session:', err);
                        await logout();
                    }
                }
            } else {
                setFirebaseUser(null);
                setUser(null);
            }

            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [logout]);

    const value: AuthContextType = {
        user,
        firebaseUser,
        isAuthenticated: !!user && !consentRequired,
        isLoading,
        consentRequired,
        surveyCompleted,
        error,
        initializeAuth,
        signInWithGoogle,
        recordConsent,
        logout,
        clearError,
        checkSurveyStatus,
        getIdToken
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
